export interface VerifiableLicense {
  id: number;
  license_number: string;
  hash_code: string; // Unique hash for QR code URL
  holder_name: string;
  holder_photo: string;
  date_of_birth: string;
  nationality: string;
  license_category: 'PILOT' | 'CABIN_CREW' | 'FLIGHT_DISPATCHER' | 'AME' | 'ATC' | 'ATSEP' | 'ASO';
  issue_date: string;
  expiry_date: string;
  status: 'ACTIVE' | 'EXPIRED' | 'SUSPENDED' | 'REVOKED';
  ratings?: string[];
  restrictions?: string[];
  medical_class?: string;
  medical_expiry?: string;
  issuing_authority: string;
  verification_count: number;
  last_verified?: string;
}

export interface Inspector {
  id: number;
  email: string;
  password: string; // In real system, this would be hashed
  full_name: string;
  badge_number: string;
  organization: string;
  phone: string;
  status: 'ACTIVE' | 'SUSPENDED';
  created_at: string;
}

export interface OTPSession {
  inspector_id: number;
  otp: string;
  expires_at: string;
  verified: boolean;
}

// Demo verifiable licenses
export const mockVerifiableLicenses: VerifiableLicense[] = [
  {
    id: 1,
    license_number: 'NCAA-PILOT-2024-001234',
    hash_code: 'abc123def456',
    holder_name: 'John Chukwuma Okafor',
    holder_photo: '/placeholder-avatar.jpg',
    date_of_birth: '1990-05-15',
    nationality: 'Nigerian',
    license_category: 'PILOT',
    issue_date: '2024-06-15',
    expiry_date: '2029-06-15',
    status: 'ACTIVE',
    ratings: ['PPL(A)', 'CPL(A)', 'Multi-Engine Land', 'Instrument Rating'],
    restrictions: ['Must wear corrective lenses'],
    medical_class: 'Class 1',
    medical_expiry: '2025-12-31',
    issuing_authority: 'Nigerian Civil Aviation Authority (NCAA)',
    verification_count: 15,
    last_verified: '2025-01-10T14:30:00Z',
  },
  {
    id: 2,
    license_number: 'NCAA-ATC-2023-005678',
    hash_code: 'xyz789ghi012',
    holder_name: 'Amina Fatima Bello',
    holder_photo: '/placeholder-avatar.jpg',
    date_of_birth: '1988-03-20',
    nationality: 'Nigerian',
    license_category: 'ATC',
    issue_date: '2023-08-20',
    expiry_date: '2028-08-20',
    status: 'ACTIVE',
    ratings: ['Tower Control', 'Approach Control', 'Area Control'],
    restrictions: [],
    medical_class: 'ATC Medical',
    medical_expiry: '2026-06-30',
    issuing_authority: 'Nigerian Civil Aviation Authority (NCAA)',
    verification_count: 8,
    last_verified: '2025-01-08T09:15:00Z',
  },
  {
    id: 3,
    license_number: 'NCAA-CABIN-2024-003456',
    hash_code: 'demo123test456',
    holder_name: 'Emmanuel Eze',
    holder_photo: '/placeholder-avatar.jpg',
    date_of_birth: '1995-11-10',
    nationality: 'Nigerian',
    license_category: 'CABIN_CREW',
    issue_date: '2024-03-10',
    expiry_date: '2026-03-10',
    status: 'ACTIVE',
    ratings: ['Safety & Emergency Procedures', 'First Aid', 'Dangerous Goods'],
    restrictions: [],
    medical_class: 'Class 2',
    medical_expiry: '2025-09-30',
    issuing_authority: 'Nigerian Civil Aviation Authority (NCAA)',
    verification_count: 22,
    last_verified: '2025-01-12T11:45:00Z',
  },
];

// Demo inspectors
export const mockInspectors: Inspector[] = [
  {
    id: 1,
    email: 'inspector@ncaa.gov.ng',
    password: 'inspector123', // Demo password
    full_name: 'Inspector General Musa',
    badge_number: 'NCAA-INS-001',
    organization: 'Nigerian Civil Aviation Authority',
    phone: '+234 803 555 1234',
    status: 'ACTIVE',
    created_at: '2023-01-15T00:00:00Z',
  },
  {
    id: 2,
    email: 'verifier@ncaa.gov.ng',
    password: 'verifier123',
    full_name: 'Sarah Adeyemi',
    badge_number: 'NCAA-INS-002',
    organization: 'Nigerian Civil Aviation Authority',
    phone: '+234 805 555 6789',
    status: 'ACTIVE',
    created_at: '2023-06-20T00:00:00Z',
  },
];

// OTP sessions (in-memory storage for demo)
let otpSessions: OTPSession[] = [];

// Helper functions
export const getLicenseByHashCode = (hashCode: string): VerifiableLicense | undefined => {
  return mockVerifiableLicenses.find(license => license.hash_code === hashCode);
};

export const getInspectorByEmail = (email: string): Inspector | undefined => {
  return mockInspectors.find(inspector => inspector.email === email);
};

export const authenticateInspector = (email: string, password: string): Inspector | null => {
  const inspector = mockInspectors.find(
    i => i.email === email && i.password === password && i.status === 'ACTIVE'
  );
  return inspector || null;
};

export const generateOTP = (): string => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

export const createOTPSession = (inspectorId: number): string => {
  const otp = generateOTP();
  const expiresAt = new Date();
  expiresAt.setMinutes(expiresAt.getMinutes() + 10); // OTP valid for 10 minutes

  const session: OTPSession = {
    inspector_id: inspectorId,
    otp,
    expires_at: expiresAt.toISOString(),
    verified: false,
  };

  // Remove any existing sessions for this inspector
  otpSessions = otpSessions.filter(s => s.inspector_id !== inspectorId);
  otpSessions.push(session);

  console.log('🔐 Mock OTP generated:', otp, 'for inspector:', inspectorId);
  return otp;
};

export const verifyOTP = (inspectorId: number, otp: string): boolean => {
  const session = otpSessions.find(
    s => s.inspector_id === inspectorId && s.otp === otp && !s.verified
  );

  if (!session) return false;

  const now = new Date();
  const expiresAt = new Date(session.expires_at);

  if (now > expiresAt) {
    // OTP expired
    return false;
  }

  // Mark as verified
  session.verified = true;
  return true;
};

export const incrementVerificationCount = (hashCode: string): void => {
  const license = getLicenseByHashCode(hashCode);
  if (license) {
    license.verification_count++;
    license.last_verified = new Date().toISOString();
  }
};
